import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { Search, X, Tag, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useI18n, useLocalized } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { useTheme } from "@/lib/theme-context";

const BASE_URL = import.meta.env.BASE_URL || "/";

interface SuggestionProduct {
  id: number;
  name: string;
  price: number;
  salePrice: number | null;
  imageUrl: string | null;
  categoryName: string | null;
}

interface SuggestionCategory {
  id: number;
  name: string;
  slug: string;
}

interface SuggestionsData {
  products: SuggestionProduct[];
  categories: SuggestionCategory[];
}

export function SearchAutocomplete() {
  const { t, lang } = useI18n();
  const { formatPrice } = useCurrency();
  const l = useLocalized();
  const theme = useTheme();
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SuggestionsData | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSuggestions(null);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}api/search/suggestions?q=${encodeURIComponent(q)}`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data);
        setIsOpen(true);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const instantSearch = theme?.instantSearch === "true";

  const handleInputChange = (value: string) => {
    setQuery(value);
    setActiveIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (instantSearch) {
      fetchSuggestions(value);
    } else {
      debounceRef.current = setTimeout(() => fetchSuggestions(value), 250);
    }
  };

  const navigateToSearch = (q?: string) => {
    const searchQuery = q || query;
    if (searchQuery.trim()) {
      setLocation(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsOpen(false);
      setQuery("");
      inputRef.current?.blur();
    }
  };

  const navigateToProduct = (id: number) => {
    setLocation(`/product/${id}`);
    setIsOpen(false);
    setQuery("");
  };

  const navigateToCategory = (id: number) => {
    setLocation(`/shop?category=${id}`);
    setIsOpen(false);
    setQuery("");
  };

  const getTotalItems = () => {
    if (!suggestions) return 0;
    return suggestions.products.length + suggestions.categories.length + (query.trim() ? 1 : 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const total = getTotalItems();

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < total - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : total - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (!suggestions || activeIndex < 0) {
        navigateToSearch();
        return;
      }

      let idx = activeIndex;
      if (query.trim()) {
        if (idx === 0) {
          navigateToSearch();
          return;
        }
        idx -= 1;
      }

      if (idx < suggestions.products.length) {
        navigateToProduct(suggestions.products[idx].id);
      } else {
        const catIdx = idx - suggestions.products.length;
        if (catIdx < suggestions.categories.length) {
          navigateToCategory(suggestions.categories[catIdx].id);
        }
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const hasResults =
    suggestions && (suggestions.products.length > 0 || suggestions.categories.length > 0);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (suggestions && query.length >= 2) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={lang === "ar" ? (theme?.searchPlaceholderAr || t.nav.search) : (theme?.searchPlaceholder || t.nav.search)}
          className={`h-9 w-full bg-background ps-8 pe-8 md:w-[300px] lg:w-[400px] ${
            theme?.searchShape === "pill"
              ? "rounded-full border border-input"
              : theme?.searchShape === "square"
              ? "rounded-none border border-input"
              : theme?.searchShape === "underlined"
              ? "rounded-none border-0 border-b-2 border-input bg-transparent focus-visible:ring-0 focus-visible:border-foreground"
              : theme?.searchShape === "soft"
              ? "rounded-2xl border border-input shadow-sm"
              : "rounded-md border border-input"
          }`}
          autoComplete="off"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setSuggestions(null);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute end-2.5 top-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-[10000] max-h-[400px] overflow-y-auto">
          {loading && !hasResults && (
            <div className="p-4 text-center text-sm text-muted-foreground">Searching...</div>
          )}

          {!loading && !hasResults && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No results found for "{query}"
            </div>
          )}

          {query.trim() && (
            <button
              onClick={() => navigateToSearch()}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-muted transition-colors border-b ${
                activeIndex === 0 ? "bg-muted" : ""
              }`}
            >
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>Search for "<span className="font-medium">{query}</span>"</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
            </button>
          )}

          {suggestions && suggestions.products.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30">
                Products
              </div>
              {suggestions.products.map((product, i) => {
                const itemIndex = (query.trim() ? 1 : 0) + i;
                return (
                  <button
                    key={product.id}
                    onClick={() => navigateToProduct(product.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors ${
                      activeIndex === itemIndex ? "bg-muted" : ""
                    }`}
                  >
                    {theme?.searchShowImages !== "false" && (
                      <div className="h-10 w-10 rounded bg-muted border overflow-hidden shrink-0">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name || ""} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">
                            N/A
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium truncate">{l(product, "name")}</div>
                      {product.categoryName && (
                        <div className="text-xs text-muted-foreground">{l(product, "categoryName", "categoryNameAr")}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {product.salePrice ? (
                        <div>
                          <span className="font-semibold">{formatPrice(product.salePrice)}</span>
                          <span className="text-xs text-muted-foreground line-through ml-1">
                            {formatPrice(product.price)}
                          </span>
                        </div>
                      ) : (
                        <span className="font-semibold">{formatPrice(product.price)}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {suggestions && suggestions.categories.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30">
                Categories
              </div>
              {suggestions.categories.map((cat, i) => {
                const itemIndex =
                  (query.trim() ? 1 : 0) + (suggestions?.products.length ?? 0) + i;
                return (
                  <button
                    key={cat.id}
                    onClick={() => navigateToCategory(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors ${
                      activeIndex === itemIndex ? "bg-muted" : ""
                    }`}
                  >
                    <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center shrink-0">
                      <Tag className="h-4 w-4 text-primary" />
                    </div>
                    <span className="font-medium">{l(cat, "name")}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
